import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type {
  ContactResponse, ConversationEventResponse, ConversationResponse, MessageResponse,
} from '../api/types';
import { useApi } from '../auth/ApiProvider';
import { Permissions } from '../auth/permissions';
import { usePermission, useSession } from '../auth/SessionProvider';
import { useI18n } from '../i18n/I18nProvider';
import { useLimits } from '../hooks/LimitsProvider';
import { useContactRole, useRoleLabel } from '../i18n/roles';
import { mergeTimeline } from '../media/threadTimeline';
import type { Recording } from '../media/voiceRecorder';
import { ChatImageButton, MessageAttachments, SharePicker, VoiceNoteButton } from './ChatMedia';
import { Input, Select } from './Field';
import { Avatar } from './Photo';
import { Badge, Button, Card, Notice, Row, Stack } from './primitives';
import { EmptyState, ErrorState, LoadingState } from './states';

/**
 * Threaded messaging (§4.7), shared by the guardian and staff applications.
 *
 * One component because both audiences want the same thing — a list of threads, a thread, a box to type
 * in — and the difference between them is entirely in who the server says they may talk to. The contact
 * picker is populated from that same answer, so the interface never offers someone a message would be
 * refused for.
 *
 * There is no reply box on a broadcast. A guardian replying to an announcement opens a direct thread with
 * whoever sent it, so one family's question does not land in every family's inbox (FR-MSG-10).
 *
 * An open thread and the conversation list poll for new messages at the configured intervals (`polling`
 * from `useLimits`, decision 2026-09-23 #23). TanStack Query stops the timer while the tab is hidden, so a
 * phone in a pocket is not polling.
 */
export function Messaging({ childContextId }: { childContextId?: number }) {
  const api = useApi();
  const { t } = useI18n();
  const { polling } = useLimits();
  // A notification's link names the thread (`?conversation=`), so a tap on a push opens it rather than the list.
  const [searchParams, setSearchParams] = useSearchParams();
  const [openId, setOpenId] = useState<number | null>(() => {
    const named = Number(searchParams.get('conversation'));
    return Number.isInteger(named) && named > 0 ? named : null;
  });
  const [composing, setComposing] = useState(false);

  /** Back to the list, dropping the thread from the address so going back to this screen shows the list. */
  const closeThread = () => {
    setOpenId(null);
    if (searchParams.has('conversation')) {
      const rest = new URLSearchParams(searchParams);
      rest.delete('conversation');
      setSearchParams(rest, { replace: true });
    }
  };

  const conversations = useQuery({
    queryKey: ['conversations'],
    queryFn: () => api.conversations.list(),
    refetchInterval: polling.listMs,
  });

  // The open thread's own row, not a search of the list: a thread opened from a notification may be past its first page.
  const open = useQuery({
    queryKey: ['conversations', openId],
    queryFn: () => api.conversations.get(openId!),
    enabled: openId !== null,
  });

  if (openId !== null) {
    return (
      <Thread
        conversationId={openId}
        childContextId={open.data?.childContextId ?? null}
        onBack={closeThread}
      />
    );
  }

  if (composing) {
    return (
      <Compose
        childContextId={childContextId ?? null}
        onCancel={() => setComposing(false)}
        onStarted={(conversationId) => { setComposing(false); setOpenId(conversationId); }}
      />
    );
  }

  if (conversations.isPending) return <LoadingState />;
  if (conversations.isError) {
    return <ErrorState error={conversations.error} onRetry={() => void conversations.refetch()} />;
  }

  return (
    <Stack>
      <Row between>
        <h1>{t('messages.title')}</h1>
        <Button onClick={() => setComposing(true)}>{t('messages.new')}</Button>
      </Row>

      {conversations.data.items.length === 0
        ? <EmptyState icon="💬" title={t('messages.empty')} />
        : conversations.data.items.map((conversation) => (
          <ConversationRow
            key={conversation.id}
            conversation={conversation}
            onOpen={() => setOpenId(conversation.id)}
          />
        ))}
    </Stack>
  );
}

function ConversationRow({ conversation, onOpen }: {
  conversation: ConversationResponse; onOpen: () => void;
}) {
  const { t, formatDateTime } = useI18n();
  const { session } = useSession();

  // Everyone except the reader. A thread listed as "me" tells nobody anything.
  const otherParticipants = conversation.participants
    .filter((participant) => participant.membershipId !== session?.membership.membershipId);
  const others = otherParticipants
    .map((participant) => participant.name)
    .filter(Boolean)
    .join('، ');
  // A message that is only a photo or a voice note has no text to preview.
  const preview = conversation.lastMessagePreview
    || (conversation.lastMessageAt ? t('messages.attachment') : null);

  return (
    <button type="button" className="dg-child-card" onClick={onOpen}>
      <Avatar name={otherParticipants[0]?.name} photoUrl={otherParticipants[0]?.photoUrl} />
      <span className="dg-child-card__body">
        <span className="dg-child-card__name">{others || conversation.subject || '—'}</span>
        {conversation.childContextName && (
          <span className="dg-child-card__meta">
            {t('messages.about', { name: conversation.childContextName })}
          </span>
        )}
        {preview && <span className="dg-child-card__meta">{preview}</span>}
      </span>
      <span className="dg-spacer" />
      {conversation.lastMessageAt && (
        <span className="dg-muted dg-text-sm">{formatDateTime(conversation.lastMessageAt)}</span>
      )}
      {conversation.unreadCount > 0 && <Badge tone="count">{conversation.unreadCount}</Badge>}
    </button>
  );
}

function Thread({ conversationId, childContextId, onBack }: {
  conversationId: number;
  childContextId: number | null;
  onBack: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { session } = useSession();
  const isStaff = usePermission(Permissions.messages.direct);
  const { polling } = useLimits();
  const [draft, setDraft] = useState('');
  const [sharing, setSharing] = useState(false);

  const messages = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => api.conversations.messages(conversationId),
    refetchInterval: polling.threadMs,
  });

  const latestId = messages.data?.items.at(-1)?.id;
  const oldest = messages.data?.items[0]?.sentAt;

  // The child's events, for the same stretch of time as the messages on screen (decision 2026-09-23 #24).
  const events = useQuery({
    queryKey: ['conversation-events', conversationId, oldest],
    queryFn: () => api.conversations.events(conversationId, oldest ? { from: oldest } : {}),
    enabled: childContextId !== null && messages.isSuccess,
    refetchInterval: polling.listMs,
  });

  // Reading the thread is what marks it read; a separate button would be one nobody presses. Kept out of
  // the query function on purpose — that runs again on every refetch and retry, so a write placed there
  // is a write nobody asked for, posted repeatedly behind the reader's back. It runs again when a new
  // message arrives while the thread is open, because the reader has now seen that one too.
  useEffect(() => {
    api.conversations.markRead(conversationId)
      // The badge on "Messages" and the bell both count what was just read.
      .then(() => Promise.all([
        queryClient.invalidateQueries({ queryKey: ['conversations'] }),
        queryClient.invalidateQueries({ queryKey: ['notifications'] }),
      ]))
      .catch(() => {
        // Marking read is a convenience. Failing it must not put an error over a thread that displayed.
      });
  }, [api, queryClient, conversationId, latestId]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
    await queryClient.invalidateQueries({ queryKey: ['conversations'] });
  };

  const send = useMutation({
    mutationFn: (input: { body: string; photoIds?: number[] }) =>
      api.conversations.send(conversationId, input.body, input.photoIds),
    onSuccess: async () => {
      setDraft('');
      setSharing(false);
      await refresh();
    },
  });

  const sendVoice = useMutation({
    mutationFn: (recording: Recording) =>
      api.conversations.sendVoiceNote(conversationId, recording.blob, recording.durationMs),
    onSuccess: refresh,
  });

  // What is typed in the message box goes with the picture, as its caption.
  const sendImage = useMutation({
    mutationFn: (image: Blob) => api.conversations.sendImage(conversationId, image, draft.trim()),
    onSuccess: async () => {
      setDraft('');
      await refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (messageId: number) => api.conversations.deleteMessage(conversationId, messageId),
    onSuccess: refresh,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (draft.trim()) send.mutate({ body: draft.trim() });
  };

  const error = send.error ?? sendVoice.error ?? sendImage.error ?? remove.error;

  return (
    <Stack>
      <Button variant="ghost" onClick={onBack}>{t('action.back')}</Button>

      {messages.isPending && <LoadingState />}
      {messages.isError && (
        <ErrorState error={messages.error} onRetry={() => void messages.refetch()} />
      )}

      {messages.data && (
        <Stack>
          {mergeTimeline(messages.data.items, events.data ?? []).map((item) => item.kind === 'event'
            ? <EventLine key={`event-${item.event.notificationId}`} event={item.event} />
            : (
              <MessageBubble
                key={item.message.id}
                message={item.message}
                mine={item.message.senderMembershipId === session?.membership.membershipId}
                deleting={remove.isPending && remove.variables === item.message.id}
                onDelete={() => remove.mutate(item.message.id)}
              />
            ))}
        </Stack>
      )}

      {error && <ErrorState error={error} />}

      {sharing && (
        <SharePicker
          conversationId={conversationId}
          busy={send.isPending}
          onShare={(photoIds) => send.mutate({ body: draft.trim(), photoIds })}
          onCancel={() => setSharing(false)}
        />
      )}

      <form onSubmit={submit}>
        <Row>
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('messages.write')}
            aria-label={t('messages.write')}
          />
          <Button type="submit" busy={send.isPending} busyLabel={t('action.saving')}>
            {t('messages.send')}
          </Button>
        </Row>
      </form>

      <Row>
        <VoiceNoteButton disabled={sendVoice.isPending} onRecorded={(recording) => sendVoice.mutate(recording)} />
        {/* A family sends a picture from the phone; staff never do, because nothing can tell which children
            a staff phone's picture shows (Q46). */}
        {!isStaff && <ChatImageButton busy={sendImage.isPending} onChosen={(image) => sendImage.mutate(image)} />}
        {/* Staff share from the child's gallery; a family sees the gallery itself (decision 2026-09-23 #20). */}
        {isStaff && childContextId !== null && !sharing && (
          <Button variant="secondary" onClick={() => setSharing(true)}>{t('messages.share')}</Button>
        )}
      </Row>
    </Stack>
  );
}

/**
 * One of the reader's notifications about the child, as a grey line between the messages. It leads where
 * the notification leads, where that is a screen of this app.
 */
function EventLine({ event }: { event: ConversationEventResponse }) {
  const { formatDateTime } = useI18n();
  const text = event.subject ?? event.body;
  const time = <span className="dg-muted"> · {formatDateTime(event.at)}</span>;

  return (
    <p className="dg-thread-event">
      {event.deepLink?.startsWith('/')
        ? <Link to={event.deepLink}>{text}</Link>
        : <span>{text}</span>}
      {time}
    </p>
  );
}

function MessageBubble({ message, mine, deleting, onDelete }: {
  message: MessageResponse;
  mine: boolean;
  deleting: boolean;
  onDelete: () => void;
}) {
  const { t, formatDateTime } = useI18n();
  const roleLabel = useRoleLabel();
  const [confirming, setConfirming] = useState(false);
  const role = roleLabel(message.senderRoleKey);

  return (
    <div className={`dg-message${mine ? ' dg-message--mine' : ''}`}>
      <Row>
        <Avatar name={message.senderName} photoUrl={message.senderPhotoUrl} size="sm" />
        <p className="dg-message__meta">
          {/* Name and role together: "Fatima" and "Fatima, teacher" carry different weight
              when a parent is reading about their child (FR-MSG-04). */}
          <strong>{message.senderName}</strong>
          {role && <span className="dg-muted"> · {role}</span>}
          <span className="dg-muted"> · {formatDateTime(message.sentAt)}</span>
        </p>
      </Row>
      {message.body && <p className="dg-message__body">{message.body}</p>}
      <MessageAttachments attachments={message.attachments ?? []} />
      {mine && (
        <Row>
          <p className="dg-message__meta dg-muted">
            {message.readByAll ? t('messages.read') : t('messages.delivered')}
          </p>
          <span className="dg-spacer" />
          {confirming
            ? (
              <>
                <Button variant="danger" busy={deleting} onClick={onDelete}>{t('messages.delete.confirm')}</Button>
                <Button variant="ghost" disabled={deleting} onClick={() => setConfirming(false)}>
                  {t('action.cancel')}
                </Button>
              </>
            )
            : <Button variant="ghost" onClick={() => setConfirming(true)}>{t('messages.delete')}</Button>}
        </Row>
      )}
    </div>
  );
}

function Compose({ childContextId, onCancel, onStarted }: {
  childContextId: number | null;
  onCancel: () => void;
  onStarted: (conversationId: number) => void;
}) {
  const api = useApi();
  const { t } = useI18n();
  const contactRole = useContactRole();
  const [recipient, setRecipient] = useState<number | ''>('');
  const [body, setBody] = useState('');

  const contacts = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.conversations.contacts(),
  });

  const start = useMutation({
    mutationFn: (input: { membershipId: number; body: string; childId: number | null }) =>
      api.conversations.start({
        participantMembershipIds: [input.membershipId],
        childContextId: input.childId,
        subject: null,
        body: input.body,
      }),
    onSuccess: (message) => onStarted(message.conversationId),
  });

  const chosen = (contacts.data ?? []).find((contact) => contact.membershipId === recipient);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (recipient === '' || !body.trim()) return;
    start.mutate({
      membershipId: recipient,
      body: body.trim(),
      childId: chosen?.childContextId ?? childContextId,
    });
  };

  return (
    <Card title={t('messages.new')}>
      <form onSubmit={submit}>
        <Stack>
          {contacts.isError && <ErrorState error={contacts.error} />}
          {start.isError && <ErrorState error={start.error} />}

          {contacts.data?.length === 0 && (
            <Notice tone="info">{t('messages.empty')}</Notice>
          )}

          <Select
            aria-label={t('messages.to')}
            value={recipient}
            onChange={(event) =>
              setRecipient(event.target.value === '' ? '' : Number(event.target.value))}
          >
            <option value="">{t('messages.to')}</option>
            {(contacts.data ?? []).map((contact) => (
              <option key={contact.membershipId} value={contact.membershipId}>
                {contactLabel(contact, contactRole(contact))}
              </option>
            ))}
          </Select>

          <Input
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder={t('messages.write')}
            aria-label={t('messages.write')}
          />

          <Row>
            <Button
              type="submit"
              busy={start.isPending}
              busyLabel={t('action.saving')}
              disabled={recipient === '' || !body.trim()}
            >
              {t('messages.send')}
            </Button>
            <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}

function contactLabel(contact: ContactResponse, roleName: string | null): string {
  const name = contact.name ?? '—';
  const role = roleName ? ` · ${roleName}` : '';
  const child = contact.childContextName ? ` · ${contact.childContextName}` : '';
  return `${name}${role}${child}`;
}
